import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronDownIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { PlayIcon } from "@heroicons/react/24/solid";
import { Tooltip } from "react-tooltip";
import {
  endpointsForContext,
  missingRequiredFields,
  pickPreferredToken,
  placeholderForParam,
  resolveEndpointPath,
} from "../../constants/apiEndpoints";
import { apiTokensService } from "../../services/apiTokens";
import {
  buildExternalRequestUrl,
  buildSnippet,
  runExternalApiRequest,
} from "../../services/externalApi";
import { useToast } from "../../contexts/ToastContext";
import OrbitalLoader from "../common/OrbitalLoader";
import ToggleSwitch from "../patterns/ToggleSwitch";
import ApiHelpLinks from "./ApiHelpLinks";

const METHOD_COLORS = {
  GET: "bg-emerald-500/20 text-emerald-400",
  POST: "bg-sky-500/20 text-sky-400",
  PUT: "bg-amber-500/20 text-amber-400",
  PATCH: "bg-amber-500/20 text-amber-400",
  DELETE: "bg-rose-500/20 text-rose-400",
};

function MethodBadge({ method, size = "sm" }) {
  const colors = METHOD_COLORS[method] || "bg-theme-secondary text-theme-text";
  const sizeClass = size === "xs" ? "text-[10px] px-1.5 py-0.5" : "text-xs px-2 py-0.5";
  return (
    <span className={`font-mono font-semibold rounded ${sizeClass} ${colors}`}>
      {method}
    </span>
  );
}

const sectionLabelClass = "block text-xs font-medium text-theme-text opacity-70 mb-1";

const ApiPlayground = ({
  context = "brand",
  currentBrand,
  brands = [],
  audienceHash = "",
  projectHash = "",
}) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const endpoints = useMemo(() => endpointsForContext(context), [context]);
  const [tokens, setTokens] = useState([]);
  const [tokensLoaded, setTokensLoaded] = useState(false);
  const [selectedEndpointId, setSelectedEndpointId] = useState(endpoints[0]?.id);
  const [selectedTokenHash, setSelectedTokenHash] = useState("");
  const [selectedBrandHash, setSelectedBrandHash] = useState(currentBrand?.hash || "");
  const [fieldValues, setFieldValues] = useState({});
  const [snippetMode, setSnippetMode] = useState("curl");
  const [running, setRunning] = useState(false);
  const [response, setResponse] = useState(null);
  const [copied, setCopied] = useState("");
  const [sessionKey, setSessionKey] = useState("");
  const [sessionState, setSessionState] = useState("idle");
  const [endpointMenuOpen, setEndpointMenuOpen] = useState(false);
  const endpointMenuRef = useRef(null);

  useEffect(() => {
    apiTokensService.listTokens(
      (res) => {
        const rows = res.data?.results || res.data || [];
        setTokens(Array.isArray(rows) ? rows : []);
        setTokensLoaded(true);
      },
      () => {
        setTokens([]);
        setTokensLoaded(true);
      },
    );
  }, []);

  useEffect(() => {
    if (currentBrand?.hash) setSelectedBrandHash(currentBrand.hash);
  }, [currentBrand?.hash]);

  useEffect(() => {
    setSelectedEndpointId(endpoints[0]?.id);
  }, [endpoints]);

  useEffect(() => {
    if (!endpointMenuOpen) return;
    const onClick = (event) => {
      if (!endpointMenuRef.current?.contains(event.target)) {
        setEndpointMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [endpointMenuOpen]);

  const selectedEndpoint = endpoints.find((e) => e.id === selectedEndpointId) || endpoints[0];
  const endpointNeedsBrand = (selectedEndpoint?.fields || []).some((field) => field.source === "brand");

  useEffect(() => {
    const preferred = pickPreferredToken(tokens, {
      brandHash: selectedBrandHash || currentBrand?.hash,
      projectHash: projectHash || currentBrand?.project_hash,
    });
    if (preferred && !selectedTokenHash) {
      setSelectedTokenHash(preferred.hash);
    } else if (preferred && !tokens.find((t) => t.hash === selectedTokenHash && t.is_active)) {
      setSelectedTokenHash(preferred.hash);
    }
  }, [tokens, selectedBrandHash, currentBrand, projectHash, selectedTokenHash]);

  useEffect(() => {
    if (!selectedEndpoint) return;
    setFieldValues((prev) => {
      const next = { ...prev };
      selectedEndpoint.fields.forEach((field) => {
        if (field.type === "boolean" && next[field.key] === undefined) {
          next[field.key] = field.default === true;
        }
        if (
          field.type === "select" &&
          field.options?.length &&
          (next[field.key] === undefined || next[field.key] === "")
        ) {
          next[field.key] = field.default ?? field.options[0].value;
        }
        if (field.source === "brand" && selectedBrandHash) {
          next[field.key] = selectedBrandHash;
        }
        if (field.source === "audience" && audienceHash) {
          next[field.key] = audienceHash;
        }
        // Prefill editable brand_hash on audience highlights when a brand is selected
        if (
          field.key === "brand_hash" &&
          !field.source &&
          selectedBrandHash &&
          (next[field.key] === undefined || next[field.key] === "")
        ) {
          next[field.key] = selectedBrandHash;
        }
      });
      return next;
    });
  }, [selectedEndpoint, selectedBrandHash, audienceHash]);

  const activeTokens = tokens.filter((token) => token.is_active);
  const selectedToken = tokens.find((t) => t.hash === selectedTokenHash);

  useEffect(() => {
    if (!selectedTokenHash || !selectedToken?.is_active) {
      setSessionKey("");
      setSessionState(selectedTokenHash ? "error" : "idle");
      return undefined;
    }
    let cancelled = false;
    const storageKey = `playground-api-token:v2:${selectedTokenHash}`;
    const cached = JSON.parse(sessionStorage.getItem(storageKey) || "null");
    if (cached?.token && new Date(cached.expiresAt).getTime() > Date.now()) {
      setSessionKey(cached.token);
      setSessionState("ready");
      return undefined;
    }
    sessionStorage.removeItem(storageKey);
    setSessionKey("");
    setSessionState("loading");
    apiTokensService.playgroundSession(
      selectedTokenHash,
      (res) => {
        if (cancelled) return;
        const token = res.data?.token || "";
        if (token) {
          sessionStorage.setItem(storageKey, JSON.stringify({
            token,
            expiresAt: res.data.expires_at,
          }));
        }
        setSessionKey(token);
        setSessionState(token ? "ready" : "error");
      },
      () => {
        if (cancelled) return;
        setSessionKey("");
        setSessionState("error");
      },
    );
    return () => {
      cancelled = true;
    };
  }, [selectedTokenHash, selectedToken?.is_active]);
  const pathValues = {};
  const query = {};
  const displayQuery = {};
  (selectedEndpoint?.fields || []).forEach((field) => {
    if (field.type === "boolean") {
      const on = fieldValues[field.key] === true;
      const defaultOn = field.default === true;
      // Omit optional booleans at their default so URLs stay lean.
      if (!field.required && on === defaultOn) return;
      query[field.key] = on ? "true" : "false";
      displayQuery[field.key] = on ? "true" : "false";
      return;
    }
    const value = (fieldValues[field.key] || "").trim();
    if (field.query) {
      if (value) {
        const atDefault =
          !field.required &&
          field.default !== undefined &&
          value === String(field.default);
        if (!atDefault) {
          query[field.key] = value;
          displayQuery[field.key] = value;
        }
      } else if (field.required) {
        displayQuery[field.key] = placeholderForParam(field.key);
      }
    } else {
      pathValues[field.key] = value;
    }
  });

  const displayPath = selectedEndpoint
    ? resolveEndpointPath(selectedEndpoint, pathValues, { usePlaceholders: true })
    : "/";
  const runPath = selectedEndpoint
    ? resolveEndpointPath(selectedEndpoint, pathValues)
    : "/";
  const displayUrl = buildExternalRequestUrl({ path: displayPath, query: displayQuery });
  const runUrlPath = runPath;
  const apiKey = sessionKey;
  const snippet = buildSnippet({
    mode: snippetMode,
    method: selectedEndpoint?.method,
    url: displayUrl,
    apiKey: apiKey || "YOUR_API_KEY",
  });

  const missingFields = missingRequiredFields(selectedEndpoint, fieldValues);
  const hasApiKey = Boolean(apiKey);
  let runDisabledReason = "";
  if (!hasApiKey) {
    runDisabledReason = sessionState === "loading"
      ? "Loading playground key"
      : "Add an API key to run requests";
  } else if (missingFields.length) {
    runDisabledReason = `Missing: ${missingFields.map((f) => f.label).join(", ")}`;
  } else if (running) {
    runDisabledReason = "Request in progress";
  }
  const canRun = hasApiKey && !missingFields.length && !running;

  const copyText = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
      if (key === "snippet") {
        showToast(
          "Snippet copied. Playground key lasts 1 hour. Use your Account > API key for ongoing access.",
          { duration: 7000 },
        );
      }
    } catch (_) {}
  };

  const runRequest = async () => {
    if (!canRun || !selectedEndpoint) return;
    setRunning(true);
    setResponse(null);
    try {
      const res = await runExternalApiRequest({
        method: selectedEndpoint.method,
        path: runUrlPath,
        apiKey,
        query,
      });
      setResponse({
        status: res.status,
        data: res.data,
      });
    } catch (err) {
      setResponse({
        status: null,
        data: { error: err?.message || "Request failed" },
      });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 h-[calc(100vh-11rem)] min-h-[28rem]">
      {tokensLoaded && !activeTokens.length && (
        <div className="shrink-0 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-theme-text">
          You do not have any API keys yet.{" "}
          <Link to="/account/api/" className="text-theme-primary underline hover:opacity-80">
            Create one in Account → API
          </Link>{" "}
          to use the playground.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 min-h-0">
        <div className="flex flex-col gap-4 lg:col-span-1 min-h-0">
          <div ref={endpointMenuRef} className="relative shrink-0">
            <label className="text-xs font-medium text-theme-text opacity-70 mb-1 block">Endpoint</label>
            <button
              type="button"
              onClick={() => setEndpointMenuOpen((open) => !open)}
              className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-theme-secondary bg-theme-main text-theme-text text-sm hover:border-theme-primary/50"
            >
              {selectedEndpoint ? (
                <>
                  <MethodBadge method={selectedEndpoint.method} />
                  <span className="flex-1 text-left truncate">{selectedEndpoint.label}</span>
                </>
              ) : (
                <span className="flex-1 text-left opacity-60">Select an endpoint</span>
              )}
              <ChevronDownIcon
                className={`h-4 w-4 opacity-60 transition-transform ${endpointMenuOpen ? "rotate-180" : ""}`}
              />
            </button>
            {endpointMenuOpen && (
              <div className="absolute z-30 mt-1 w-full max-h-80 overflow-y-auto rounded-lg border border-theme-secondary bg-theme-main shadow-lg py-1">
                {endpoints.map((endpoint, index) => {
                  const showSection =
                    endpoint.section && endpoint.section !== endpoints[index - 1]?.section;
                  return (
                    <React.Fragment key={endpoint.id}>
                      {showSection && (
                        <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-theme-text opacity-50">
                          {endpoint.section}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedEndpointId(endpoint.id);
                          setEndpointMenuOpen(false);
                        }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-theme-secondary/60 ${
                          selectedEndpointId === endpoint.id ? "bg-theme-secondary/40" : ""
                        }`}
                      >
                        <MethodBadge method={endpoint.method} size="xs" />
                        <span className="text-theme-text truncate">{endpoint.label}</span>
                        <span className="ml-auto text-[10px] font-mono text-theme-text opacity-40 truncate max-w-[40%]">
                          {endpoint.path}
                        </span>
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>

          {selectedEndpoint && (
            <div>
              <label className={sectionLabelClass}>Parameters</label>
              <div className="space-y-3 border border-theme-secondary rounded-lg p-4">
                <p className="text-sm text-theme-text opacity-80">{selectedEndpoint.description}</p>

                <div>
                  <label className="block text-xs font-medium text-theme-text opacity-70 mb-1">
                    API key
                  </label>
                  <select
                    value={selectedTokenHash}
                    onChange={(e) => setSelectedTokenHash(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border border-theme-secondary bg-theme-main text-theme-text text-sm"
                  >
                    {!activeTokens.length && <option value="">No API keys yet</option>}
                    {activeTokens.map((token) => (
                      <option key={token.hash} value={token.hash}>
                        {(token.name || token.hash).slice(0, 40)} ({token.scope})
                      </option>
                    ))}
                  </select>
                </div>

                {(context !== "audience" || endpointNeedsBrand) && (
                  <div>
                    <label className="block text-xs font-medium text-theme-text opacity-70 mb-1">
                      Brand
                    </label>
                    <select
                      value={selectedBrandHash}
                      onChange={(e) => setSelectedBrandHash(e.target.value)}
                      className="w-full px-3 py-2 rounded-md border border-theme-secondary bg-theme-main text-theme-text text-sm"
                    >
                      {brands.map((brand) => (
                        <option key={brand.hash} value={brand.hash}>
                          {brand.name || brand.hash}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {selectedEndpoint.fields
                  .filter((field) => {
                    if (field.source === "brand" && selectedBrandHash) return false;
                    if (field.source === "audience" && audienceHash) return false;
                    return true;
                  })
                  .map((field) => (
                  <div key={field.key}>
                    <label className="block text-xs font-medium text-theme-text opacity-70 mb-1">
                      {field.label}
                      {field.required ? " *" : ""}
                    </label>
                    {field.type === "boolean" ? (
                      <ToggleSwitch
                        checked={fieldValues[field.key] === true}
                        label={fieldValues[field.key] === true ? "Yes" : "No"}
                        onChange={(on) =>
                          setFieldValues((prev) => ({ ...prev, [field.key]: on }))
                        }
                      />
                    ) : field.type === "select" ? (
                      <select
                        value={fieldValues[field.key] || field.options?.[0]?.value || ""}
                        onChange={(e) =>
                          setFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                        }
                        className="w-full px-3 py-2 rounded-md border border-theme-secondary bg-theme-main text-theme-text text-sm"
                      >
                        {(field.options || []).map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type === "number" ? "number" : "text"}
                        min={field.type === "number" ? 0 : undefined}
                        max={field.type === "number" ? 10 : undefined}
                        value={fieldValues[field.key] ?? ""}
                        placeholder={
                          field.type === "number"
                            ? "Brand default"
                            : placeholderForParam(field.key)
                        }
                        onChange={(e) =>
                          setFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                        }
                        className="w-full px-3 py-2 rounded-md border border-theme-secondary bg-theme-main text-theme-text text-sm font-mono"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-auto shrink-0 border border-theme-secondary rounded-lg p-2">
            <ApiHelpLinks
              onAgentClick={() => {
                const base = location.pathname.replace(/\/?$/, "/");
                navigate(`${base}agent-instructions/`);
              }}
            />
          </div>
        </div>

        <div className="flex flex-col gap-4 lg:col-span-2 min-h-0">
          <div className="shrink-0">
            <label className={sectionLabelClass}>Request</label>
            <div className="border border-theme-secondary rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0 text-xs font-mono break-all text-theme-text opacity-80 bg-theme-secondary bg-opacity-40 rounded p-2">
                  <MethodBadge method={selectedEndpoint?.method || "GET"} size="xs" />{" "}
                  {displayUrl}
                </div>
                <span
                  className="inline-flex shrink-0"
                  data-tooltip-id="api-playground-run-tooltip"
                  data-tooltip-content={runDisabledReason}
                  data-tooltip-hidden={!runDisabledReason}
                >
                  <button
                    type="button"
                    onClick={runRequest}
                    disabled={!canRun}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-theme-primary text-theme-button text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed ${
                      !canRun ? "pointer-events-none" : ""
                    }`}
                    style={{ backgroundColor: "var(--theme-primary)" }}
                  >
                    <PlayIcon className="w-4 h-4" />
                    Run
                  </button>
                </span>
                <Tooltip id="api-playground-run-tooltip" place="top" className="!text-xs !max-w-xs" />
              </div>
              <div className="flex gap-2">
                {["curl", "python", "javascript"].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setSnippetMode(mode)}
                    className={`px-2 py-1 rounded text-xs ${
                      snippetMode === mode
                        ? "bg-theme-secondary text-theme-text"
                        : "text-theme-text opacity-60 hover:opacity-100"
                    }`}
                  >
                    {mode}
                  </button>
                ))}
                <CopyButton
                  className="ml-auto"
                  copied={copied === "snippet"}
                  onClick={() => copyText(snippet, "snippet")}
                />
              </div>
              <pre className="text-xs font-mono whitespace-pre-wrap break-all bg-theme-secondary bg-opacity-30 rounded p-3 max-h-48 overflow-auto text-theme-text">
                {snippet}
              </pre>
              {hasApiKey && (
                <p className="flex items-center gap-1.5 text-xs text-theme-text opacity-60">
                  <ExclamationTriangleIcon className="h-4 w-4 shrink-0 text-yellow-400" aria-hidden />
                  Temporary playground key expires in 1 hour. Use a key you saved for anything that should keep working.
                </p>
              )}
            </div>
          </div>

          <div className="flex-1 min-h-0 flex flex-col">
            <label className={sectionLabelClass}>Response</label>
            <div className="border border-theme-secondary rounded-lg p-4 space-y-3 flex-1 min-h-[200px] overflow-y-auto">
              {running ? (
                <div className="flex justify-center py-8">
                  <OrbitalLoader size={64} text="Running request..." />
                </div>
              ) : response ? (
                <>
                  <div className="flex items-center gap-2">
                    <div className="text-xs text-theme-text opacity-70">
                      Status: <span className="font-semibold">{response.status ?? "error"}</span>
                    </div>
                    <CopyButton
                      className="ml-auto"
                      copied={copied === "response"}
                      onClick={() => copyText(JSON.stringify(response.data, null, 2), "response")}
                    />
                  </div>
                  <pre className="text-xs font-mono whitespace-pre-wrap break-all bg-theme-secondary bg-opacity-30 rounded p-3 max-h-96 overflow-auto text-theme-text">
                    {JSON.stringify(response.data, null, 2)}
                  </pre>
                </>
              ) : (
                <p className="text-sm text-theme-text opacity-60">Run a request to see the response.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function CopyButton({ copied, onClick, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 text-xs hover:opacity-80 ${
        copied ? "text-green-500" : "text-theme-primary"
      } ${className}`}
    >
      <ClipboardDocumentIcon className="h-4 w-4" />
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

export default ApiPlayground;
